import { ChainType, RouteResponse, Token } from "@0xsquid/squid-types";
import {
  createAssociatedTokenAccountInstruction,
  createTransferInstruction,
  getAssociatedTokenAddress,
} from "@solana/spl-token";
import { SolanaSignTransactionFeature } from "@solana/wallet-standard-features";
import { StandardWalletAdapter } from "@solana/wallet-standard-wallet-adapter-base";
import {
  Connection,
  PublicKey,
  SystemProgram,
  Transaction,
  VersionedTransaction,
} from "@solana/web3.js";
import { Wallet as StandardWallet } from "@wallet-standard/core";
import { nativeSolanaTokenAddress } from "../../core/constants";
import { SolanaConnector } from "../../core/types/solana";
import {
  ConnectorID,
  SolanaWallet,
  WindowWalletFlag,
} from "../../core/types/wallet";
import { resolveSolanaTokenProgramId } from "./solanaTokenProgramService";
import { isOnChainTxData } from "./transactionService";
import {
  isStandardWalletWithCustomFeatures,
  standardWalletOverrides,
  StandardWalletWithCustomFeatures,
} from "./walletStandardService";

type SolanaFeatures = SolanaSignTransactionFeature;
type SolanaStandardWallet = StandardWalletWithCustomFeatures<SolanaFeatures>;

const SOLANA_FEATURES: Array<keyof SolanaFeatures> = ["solana:signTransaction"];

/**
 * Validates that a given standard wallet has all the required features for a Solana wallet
 */
function isSolanaStandardWallet(
  wallet: StandardWallet
): wallet is SolanaStandardWallet {
  return isStandardWalletWithCustomFeatures(wallet, SOLANA_FEATURES);
}

export function filterSolanaWallets(
  standardWallets: readonly StandardWallet[]
): SolanaWallet[] {
  // Filter out all non-Solana standard wallets
  return standardWallets.filter(isSolanaStandardWallet).map(formatSolanaWallet);
}

/**
 * Format a Solana standard wallet into a Squid Wallet
 */
function formatSolanaWallet(wallet: SolanaStandardWallet): SolanaWallet {
  const { icon, name: defaultName, id } = wallet;

  const standardWalletId = id || defaultName;
  const walletOverrides = standardWalletOverrides[standardWalletId];
  const walletIcon = walletOverrides?.icon || icon;
  const name = walletOverrides?.name || defaultName;
  const connectorId =
    walletOverrides?.connectorId || (standardWalletId as ConnectorID);

  return {
    connectorId,
    name,
    connectorName: name,
    type: ChainType.SOLANA,
    icon: walletIcon,
    windowFlag: name as WindowWalletFlag,
    isMobile: false,
    isInstalled() {
      return true;
    },
    connector: new StandardWalletAdapter({ wallet }),
  };
}

export const isSolanaAddressValid = (address: string): boolean => {
  try {
    new PublicKey(address);
    return true;
  } catch {
    return false;
  }
};

const deserializeSolanaTransaction = (
  transactionBuffer: Buffer
): Transaction | VersionedTransaction => {
  // Squid can return legacy Solana transactions (ITS) or versioned transactions
  // (Jupiter/Chainflip). Deserialize once to detect the payload type. Legacy
  // transactions need to be converted to Transaction because their blockhash is
  // mutable and must be refreshed before signing.
  const versionedTransaction =
    VersionedTransaction.deserialize(transactionBuffer);

  return versionedTransaction.version === "legacy"
    ? Transaction.from(transactionBuffer)
    : versionedTransaction;
};

/**
 * Execute a Solana swap transaction
 * This will be used for the swap flow - Using Jupiter Dex under the hood
 */
export const executeSolanaSwap = async ({
  route,
  signer,
  connection,
  onSigned,
}: {
  route: RouteResponse["route"];
  signer: SolanaConnector;
  connection: Connection;
  onSigned?: (signature: string) => void;
}): Promise<string> => {
  if (!route.transactionRequest || !isOnChainTxData(route.transactionRequest)) {
    throw new Error("Invalid parameters");
  }

  const swapRequest = route.transactionRequest.data;
  const swapTransactionBuf = Buffer.from(swapRequest, "base64");
  const transaction = deserializeSolanaTransaction(swapTransactionBuf);

  try {
    const latestBlockhash = await connection.getLatestBlockhash();

    if (transaction instanceof Transaction) {
      // ITS legacy transactions arrive with the placeholder blockhash
      // 11111111111111111111111111111111.
      // Refresh it right before signing so the submitted transaction
      // uses a valid blockhash. Versioned transactions are not mutable here.
      transaction.recentBlockhash = latestBlockhash.blockhash;
    }

    const signature = await signer.sendTransaction(transaction, connection);

    onSigned?.(signature);

    await connection.confirmTransaction({
      signature,
      blockhash: latestBlockhash.blockhash,
      lastValidBlockHeight: latestBlockhash.lastValidBlockHeight,
    });

    return signature;
  } catch (error) {
    console.error("Solana swap failed:", error);
    throw error;
  }
};

/**
 * Execute a Solana transfer transaction for both native SOL and SPL tokens
 * @param amount - Amount to transfer in raw units (lamports for SOL, raw token units for SPL)
 * @param target - Target wallet address
 * @param signer - Solana wallet adapter instance
 * @param connection - Solana RPC connection
 * @param sourceToken - Token information (undefined for native SOL)
 * @param onSigned - Optional callback triggered when transaction is signed
 * @returns Transaction signature
 */
export const executeSolanaTransfer = async ({
  amount,
  target,
  signer,
  connection,
  sourceToken,
  onSigned,
}: {
  amount: bigint;
  target: string;
  signer: SolanaConnector;
  connection: Connection;
  sourceToken?: Token;
  onSigned?: (signature: string) => void;
}): Promise<string> => {
  // Validate signer and target address
  if (!signer.publicKey) {
    throw new Error("Signer public key not found");
  }
  if (!isSolanaAddressValid(target)) {
    throw new Error("Invalid target address");
  }

  const targetPubKey = new PublicKey(target);
  const transaction = new Transaction();
  const isNativeToken =
    !sourceToken ||
    sourceToken.address.toLowerCase() ===
      nativeSolanaTokenAddress.toLowerCase();

  if (isNativeToken) {
    // Handle native SOL transfer
    transaction.add(
      SystemProgram.transfer({
        fromPubkey: signer.publicKey,
        toPubkey: targetPubKey,
        lamports: amount,
      })
    );
  } else {
    // Handle SPL token transfer
    const mint = new PublicKey(sourceToken.address);
    const tokenProgramId = await resolveSolanaTokenProgramId({
      connection,
      token: sourceToken,
    });

    // Get Associated Token Accounts (ATAs) for both source and target
    const sourceATA = await getAssociatedTokenAddress(
      mint,
      signer.publicKey,
      true,
      tokenProgramId
    );
    const targetATA = await getAssociatedTokenAddress(
      mint,
      targetPubKey,
      true,
      tokenProgramId
    );

    // Check if target ATA exists and create if needed
    const targetAccount = await connection.getAccountInfo(targetATA);
    if (!targetAccount) {
      transaction.add(
        createAssociatedTokenAccountInstruction(
          signer.publicKey,
          targetATA,
          targetPubKey,
          mint,
          tokenProgramId
        )
      );
    }

    // Add the token transfer instruction
    transaction.add(
      createTransferInstruction(
        sourceATA,
        targetATA,
        signer.publicKey,
        amount,
        [],
        tokenProgramId
      )
    );
  }

  // Get latest blockhash and sign transaction
  const { blockhash, lastValidBlockHeight } =
    await connection.getLatestBlockhash();
  transaction.recentBlockhash = blockhash;
  transaction.feePayer = signer.publicKey;

  const signature = await signer.sendTransaction(transaction, connection);

  // Trigger callback for UI purposes (show loading state)
  onSigned?.(signature);

  // Wait for transaction confirmation
  await connection.confirmTransaction({
    signature,
    blockhash,
    lastValidBlockHeight,
  });

  return signature;
};
